import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useI18n } from '../../i18n/I18nProvider';
import { createJsonFetch } from '../accounts/accounts.api';
import { loadReportPeriods } from '../reports/reports.api';
import type { ReportPeriod } from '../reports/reports.api';
import { ClosingView } from './ClosingView';
import { closePeriod, loadClosing, reopenPeriod } from './closing.api';
import type { ClosingReport } from './closing.api';

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function ClosingScreen(): JSX.Element {
  const { authorizedFetch, session } = useAuth();
  const { t, language } = useI18n();
  const fetchJson = useMemo(() => createJsonFetch(authorizedFetch), [authorizedFetch]);

  const [periods, setPeriods] = useState<ReportPeriod[] | null>(null);
  const [periodId, setPeriodId] = useState<string | null>(null);
  const [report, setReport] = useState<ClosingReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadReportPeriods(fetchJson)
      .then((next) => {
        if (cancelled) {
          return;
        }
        setPeriods(next);
        const first = next[0];
        if (first !== undefined) {
          setPeriodId(first.id);
        } else {
          setLoading(false);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(errorText(cause));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fetchJson]);

  useEffect(() => {
    if (periodId === null) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    loadClosing(fetchJson, periodId)
      .then((next) => {
        if (!cancelled) {
          setReport(next);
          setError(null);
          setLoading(false);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setReport(null);
          setError(errorText(cause));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fetchJson, periodId]);

  const close = useCallback((): void => {
    if (periodId === null || busy) {
      return;
    }
    setBusy(true);
    closePeriod(fetchJson, periodId)
      .then((next) => {
        setReport(next);
        setError(null);
      })
      .catch((cause: unknown) => setError(errorText(cause)))
      .finally(() => setBusy(false));
  }, [busy, fetchJson, periodId]);

  const reopen = useCallback(
    (reason: string): void => {
      if (periodId === null || busy) {
        return;
      }
      setBusy(true);
      reopenPeriod(fetchJson, periodId, reason)
        .then(() => loadClosing(fetchJson, periodId))
        .then((next) => {
          setReport(next);
          setError(null);
        })
        .catch((cause: unknown) => setError(errorText(cause)))
        .finally(() => setBusy(false));
    },
    [busy, fetchJson, periodId],
  );

  const roles = session?.user.roles ?? [];

  return (
    <div className="closing-screen" data-testid="closing-screen">
      <label className="field">
        <span>{t('reportsPeriod')}</span>
        <select
          value={periodId ?? ''}
          data-testid="closing-period-picker"
          onChange={(event) => setPeriodId(event.target.value)}
        >
          {periods === null || periods.length === 0 ? <option value="">{t('reportsLoading')}</option> : null}
          {(periods ?? []).map((period) => (
            <option key={period.id} value={period.id}>
              {`${t(`closingMonth${period.month}`)} ${period.year}`}
            </option>
          ))}
        </select>
      </label>
      {error === null ? null : (
        <p className="closing__error" data-testid="closing-screen-error" role="alert">
          {error}
        </p>
      )}
      {report === null ? (
        <p className="muted" data-testid="closing-screen-loading">
          {loading ? t('reportsLoading') : t('reportsError')}
        </p>
      ) : (
        <ClosingView
          report={report}
          language={language}
          roles={roles}
          onClose={close}
          onReopen={reopen}
          busy={busy}
          error={error}
        />
      )}
    </div>
  );
}
